import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getPersonas, askPersona } from '../services/api';
import toast from 'react-hot-toast';

interface Persona {
  id: string;
  name: string;
  title: string;
  specialty: string;
  description: string;
  icon: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  persona?: string;
}

export default function CommunityPage() {
  const { currentUser } = useAuth();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selected, setSelected] = useState<Persona | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPersonas, setLoadingPersonas] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  const loadPersonas = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await getPersonas(token);
      setPersonas(res.personas || []);
    } catch {
      toast.error('Failed to load AI doctors');
    } finally {
      setLoadingPersonas(false);
    }
  }, [currentUser]);

  useEffect(() => { loadPersonas(); }, [loadPersonas]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSelectPersona = (p: Persona) => {
    setSelected(p);
    setMessages([{
      role: 'assistant',
      content: `Hello! I'm ${p.name}, a ${p.title}. ${p.description} How can I help you today? Remember, I provide general health information — always consult your personal doctor for medical decisions.`,
      persona: p.name,
    }]);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || !selected || loading || !currentUser) return;

    const userMsg: Message = { role: 'user', content: question };
    setMessages((prev) => [...prev, userMsg]);
    setQuestion('');
    setLoading(true);

    try {
      const token = await currentUser.getIdToken();
      const res = await askPersona(token, selected.id, question);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.response, persona: selected.name }]);
    } catch (e: any) {
      toast.error(e.message || 'Failed to get response');
      setMessages((prev) => prev.slice(0, -1)); // remove user message on error
      setQuestion(userMsg.content);
    } finally {
      setLoading(false);
    }
  };

  if (loadingPersonas) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Loading AI doctors...</div>;
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">AI Medical Specialists</h1>
        <p className="text-gray-500 text-sm mt-1">
          Chat with AI-powered specialist personas. General health information only — no personal data is shared.
        </p>
      </div>

      <div className="flex gap-6 h-[calc(100vh-220px)]">
        {/* Persona list */}
        <div className="w-64 flex-shrink-0 space-y-2 overflow-y-auto">
          {personas.map((p) => (
            <button
              key={p.id}
              onClick={() => handleSelectPersona(p)}
              className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                selected?.id === p.id
                  ? 'bg-emerald-50 border-emerald-300 shadow-sm'
                  : 'bg-white border-gray-200 hover:border-emerald-200 hover:bg-emerald-50/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{p.icon}</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                  <p className="text-xs text-gray-500 truncate">{p.title}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Chat window */}
        <div className="flex-1 bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center">
                <div className="text-4xl mb-3">⚕</div>
                <p>Select an AI specialist to start a conversation</p>
                <p className="text-xs mt-1 text-gray-300">General health information only</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                <span className="text-2xl">{selected.icon}</span>
                <div>
                  <p className="font-semibold text-gray-900">{selected.name}</p>
                  <p className="text-xs text-gray-500">{selected.title}</p>
                </div>
                <span className="ml-auto text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                  ⚠ General info only
                </span>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-sm mr-2 flex-shrink-0 mt-0.5">
                        {selected.icon}
                      </div>
                    )}
                    <div
                      className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-emerald-600 text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-sm mr-2">{selected.icon}</div>
                    <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSend} className="px-4 py-4 border-t border-gray-100 flex gap-2">
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={`Ask ${selected.name} a health question...`}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !question.trim()}
                  className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  Send
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
